import { Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';

import { Common } from '@components/Admin';

import useCommonSchemas from '@hooks/schemas/useCommonSchemas';

import { formatDate } from '@utils/format';

import { useAdminUpdatePermission } from 'generated/admin.generated';
import type { PermissionModel, PermissionScope } from 'generated/types';

interface MetadataEditorProps {
  permission?: PermissionModel;
  loading?: boolean;
}

const messages = defineMessages({
  name: {
    id: 'admin.permissions.name',
    defaultMessage: 'Name',
  },
  scope: {
    id: 'admin.permissions.scope',
    defaultMessage: 'Scope',
  },
  updatedAt: {
    id: 'GLOBAL.updatedAt',
    defaultMessage: 'Updated at',
  },
  createdAt: {
    id: 'GLOBAL.createdAt',
    defaultMessage: 'Created At',
  },
});

interface FormValues {
  name?: string | null;
  scope?: string | null;
}

function MetadataEditor({
  permission,
  loading: baseLoading,
}: MetadataEditorProps) {
  const { formatMessage } = useIntl();
  const [updatePermission, { loading: updatePermissionLoading }] =
    useAdminUpdatePermission();
  const { AdminSchema } = useCommonSchemas();

  const loading = baseLoading || updatePermissionLoading;

  const initialValues: FormValues = {
    name: permission?.permission_name,
    scope: permission?.permission_scope,
  };

  const handleSubmit = async (values: FormValues) => {
    if (!permission) {
      return;
    }
    await updatePermission({
      variables: {
        input: {
          permission_id: permission.permission_id,
          permission_name: values.name ?? undefined,
          permission_scope: (values.scope ?? undefined) as
            PermissionScope | undefined,
        },
      },
    });
  };

  const schema: Yup.Schema<FormValues> = Yup.object().shape({
    name: AdminSchema.name().nullable(),
    scope: AdminSchema.permissionScope.nullable(),
  });

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
      validationSchema={schema}
    >
      {({ values }) => {
        const data: Common.Card.IMetadata.Metadata[] = [
          {
            title: formatMessage(messages.scope),
            value: values.scope,
            editOptions: { name: 'scope', type: 'text' },
          },
          {
            title: formatMessage(messages.name),
            value: values.name,
            editOptions: { name: 'name', type: 'text' },
          },
          {
            title: formatMessage(messages.createdAt),
            value: formatDate({
              date: permission?.created_at,
              formatter: 'datetime',
            }),
          },
          {
            title: formatMessage(messages.updatedAt),
            value: formatDate({
              date: permission?.updated_at,
              formatter: 'datetime',
            }),
          },
        ];
        return (
          <Form>
            <Common.Card.Metadata data={data} loading={loading} />
          </Form>
        );
      }}
    </Formik>
  );
}

export default MetadataEditor;
